import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { select } from '@panel/commons/core/themev2';
import {
  sendTrackingInfo,
  Logo,
  ModalBase,
  Modal,
  TermsAndConditionsModal,
  ContactValidationModal,
  ReputationLevelModal,
  sendCustomTrackingInfo
} from '@panel/commons';
import { getMainColor, getNeutralColors } from '@panel/commons/lib/theme';
import { getContactModalPublisherType } from '@panel/commons/helpers';

import { isZonaDemandSite, isPortalWithUnifiedPanel } from '@panel/commons/helpers/siteUtils';
import SnackBarContainer from '@panel/commons/lib/SimpleSnackbar/SnackBarContainer';
import { HeaderMenuItemWrapper, HeaderMenuRight } from './headerMenu';
import { userInfoSelectors, userInfoOperations } from '../../../state/ducks/user';
import { contactValidationSelectors, contactValidationOperations } from '../../../state/ducks/contactValidation';
import { reputationLevelSelectors, reputationLevelOperations } from '../../../state/ducks/reputationLevel';
import { bumexFeaturesKeys } from '../../../constants/bumexFeatures';
import { bumexFeatureSelectors } from '../../../state/ducks/bumexFeatures';

const HeaderWrapper = styled.div`
  background-color: transparent;
  height: 60px;
  line-height: 60px;
  font-size: 1.4rem;
  margin: 0;
  border-bottom: 1px solid ${getNeutralColors('grisClaroII')};
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 100%;
  max-width: 1480px;
  @media ${({ theme }) => theme.v2.breakpoints.Min.desktop} {
    max-width: 1280px;
    margin: auto;
  }
  @media (min-width: 1480px) {
    max-width: 1480px;
  }
`;

const LogoContainer = styled.a`
  height: 60px;
  cursor: pointer;
  margin: 0px 32px 0 10px;
  img {
    vertical-align: middle;
  }
`;

const ButtonLogin = styled.div`
  height: 40px;
  display: block;
  font-size: 14px;
  font-weight: normal;
  line-height: 40px;
  padding: 0px 12px;
  margin: 10px;
  border-radius: 8px;
  background-color: ${({ baseColor }) => baseColor || select('primary')};
  color: ${select('white')};
  font-weight: 600;
  cursor: pointer;
  :hover {
    background-color: ${({ baseColor }) => baseColor || select('darkOrangeAux')};
  }
`;

function HeaderDesktop(props) {
  const site = process.env.REACT_APP_BUILD_SITE;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const user = useSelector(userInfoSelectors.getuserInfoState);
  const { getContactValidationData } = useSelector(contactValidationSelectors.getContactValidationState);
  const { levelValue } = useSelector(reputationLevelSelectors.reputationLevelState);
  const {
    hasAcceptedTermsAndconditions,
    fetchedAcceptTermsAndConditions,
    errorAcceptTermsAndConditions,
    isSeeker,
    publisherTypeId,
    isLoading,
    email,
    hasConfirmedContactData,
    userLoggedId = ''
  } = user;
  const logo = {
    name: site,
    height: 60
  };
  const { name, width, height, mainColor, hoverColor } = logo;
  const isLandingZD = window.location.href.includes('zona-demand') && isZonaDemandSite;
  const isSheredDesempeno = window.location.href.includes('compartir-desempeno');
  const baseColor = ['CMBR', 'VAMX'].includes(site) ? getMainColor() : '';
  const userIsParticular = publisherTypeId === '1' || isSeeker;
  const [showModalTermsAndConditions, setShowModalTermsAndConditions] = useState(false);
  const [showModalContactValidation, setShowModalContactValidation] = useState(false);
  const [showModalReputationLevel, setShowModalReputationLevel] = useState(false);
  const [finishedAction, setFinishedAction] = useState({ action: '', result: '' });
  const contactPublisherTypeEnable = getContactModalPublisherType(publisherTypeId) && !isSeeker;
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);
  const isPortalWithNewReputation = bumexFeatures.get(bumexFeaturesKeys.NEW_REPUTATION_SECTION);
  const isPortalWithContactModal = bumexFeatures.get(bumexFeaturesKeys.ENABLED_CONTACT_DATA_MODAL);
  const contactValidationCookie = Cookies.get('contactValidationSeen');
  const reputationLevelCookie = Cookies.get('reputationModalLevelSeen2');
  const actionFromNewPublisherCookie = Cookies.get('postingSavedInfoCrossPanel');
  const savedActionFromNewPublisherCookie = Cookies.get('postingSavedMessage');

  const isEnableTermsAndConditions =
    !isLoading &&
    // Se valida con el email que el usuario esté logueado:
    email &&
    // Se valida que el usuario no sea particular:
    !userIsParticular &&
    // Se valida desde userInfo que no se hayan aceptado Términos y Condiciones:
    !hasAcceptedTermsAndconditions &&
    // Se valida que no se haya enviado el dispatch para aceptar Términos y Condiciones:
    !fetchedAcceptTermsAndConditions &&
    // Se valida que el servicio no de error:
    !errorAcceptTermsAndConditions &&
    isPortalWithUnifiedPanel;

  const isEnableContactValidation =
    !isLoading &&
    // Se valida con el email que el usuario esté logueado:
    email &&
    contactPublisherTypeEnable &&
    isPortalWithContactModal &&
    // Se valida con el hasConfirmedContactData que el usuario no haya confirmado los datos:
    !hasConfirmedContactData;

  const isEnableReputationLevelModal =
    !isLoading &&
    // Se valida con el email que el usuario esté logueado:
    email &&
    // Se valida que el usuario no sea particular:
    !userIsParticular &&
    // Se valida el portal habilitado mediante la BF:
    isPortalWithNewReputation;

  const handleLogoClick = () => {
    sendTrackingInfo('Ir a Home');
    window.location = '/';
  };

  const handleLogin = () => {
    dispatch(userInfoOperations.showLogin(true));
    // window.location = `/login.bum?loginUrl=${window.location.href}`;
  };

  const getHeader = () => {
    if (user.isLoading && (isLandingZD || isSheredDesempeno)) return null;
    if (!user.email && (isLandingZD || isSheredDesempeno))
      return (
        <ButtonLogin dataPanel='headerMenu_btnLogin_ZD' onClick={handleLogin} baseColor={baseColor}>
          {t('panel.header.title.login')}
        </ButtonLogin>
      );
    return (
      <>
        <HeaderMenuItemWrapper
          data-panel='headerDesktop_itemList'
          user={user}
          isPortalWithNewReputation={isPortalWithNewReputation}
          isLoading={user.isLoading}
          site={site}
        />
        <HeaderMenuRight isPortalWithNewReputation={isPortalWithNewReputation} user={user} />
      </>
    );
  };

  const handleOnClickTermsAndConditions = () => dispatch(userInfoOperations.acceptTermsAndConditions());

  const onCloseContactValidationModal = () => {
    Cookies.remove('contactValidationSeen');
    Cookies.set('contactValidationSeen', true, { expires: 1 });
    setShowModalContactValidation(false);
  };

  const onCloseReputationLevelModal = () => {
    Cookies.set('reputationModalLevelSeen2', true, { expires: 400 });
    setShowModalReputationLevel(false);
    sendCustomTrackingInfo({
      event: 'click_modal',
      content_group: 'Panel',
      user_reputation: levelValue,
      user_id: userLoggedId,
      item_id: 'Siguiente',
      modal_name: 'Modal presentacion Score'
    });
  };

  useEffect(() => {
    // FETCH de datos para modal de Validación de contactos:
    if (isEnableContactValidation) dispatch(contactValidationOperations.getContactValidation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableContactValidation]);

  useEffect(() => {
    // Se valida que las condiciones estén dadas, que no exista la cookie y que ya se tengan los datos del Fetch (getContactValidationData.userPhones):
    if (isEnableContactValidation && getContactValidationData.userPhones && !contactValidationCookie) {
      setShowModalContactValidation(isEnableContactValidation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableContactValidation, getContactValidationData.userPhones, contactValidationCookie]);

  useEffect(() => {
    setShowModalTermsAndConditions(isEnableTermsAndConditions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableTermsAndConditions]);

  useEffect(() => {
    // FETCH del nivel de reputación del usuario:
    if (isEnableReputationLevelModal) dispatch(reputationLevelOperations.getReputationLevel());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableReputationLevelModal]);

  useEffect(() => {
    // Se valida que las condiciones estén dadas, que no exista la cookie y que ya se tengan el nivel de reputación del Fetch (reputationLevelSelectors.levelValue):
    const portalsExcluded = ['24MX', 'UBPE', 'CAPA', 'PLEC', 'ZPAR'].includes(site);
    if (isEnableReputationLevelModal && !!levelValue && !reputationLevelCookie && !portalsExcluded) {
      setShowModalReputationLevel(isEnableReputationLevelModal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnableReputationLevelModal, levelValue, reputationLevelCookie]);

  useEffect(() => {
    // Se lee una cookie (en vez de un parámetro por url) para evitar que se vuelva a disparar el snackbar al recargar la página.
    if (actionFromNewPublisherCookie && !savedActionFromNewPublisherCookie) {
      setFinishedAction({
        postingId: '',
        action: 'postingSaved',
        result: 'INFO',
        component: t(actionFromNewPublisherCookie)
      });
    }
    // Se elimina posteriormente la cookie:
    Cookies.remove('postingSavedInfoCrossPanel');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFromNewPublisherCookie, savedActionFromNewPublisherCookie]);

  useEffect(() => {
    // Saneamiento del state que dispara el Snackbar:
    return () => {
      setFinishedAction({ action: '', result: '' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishedAction.result]);

  return (
    <>
      <SnackBarContainer action={finishedAction} deviceType='desktop' />
      <HeaderWrapper data-panel='headerDesktop'>
        <HeaderContainer>
          <LogoContainer
            mainColor={mainColor}
            hoverColor={hoverColor}
            data-panel='headerDesktop_logo'
            onClick={handleLogoClick}
          >
            <Logo name={name} width={width} height={height} />
          </LogoContainer>
          {getHeader()}
        </HeaderContainer>
      </HeaderWrapper>
      {showModalTermsAndConditions && (
        <Modal>
          <ModalBase openModal={showModalTermsAndConditions} width='400px' withoutCloseButton>
            <TermsAndConditionsModal
              onClose={() => handleOnClickTermsAndConditions}
              handleDispatch={handleOnClickTermsAndConditions}
            />
          </ModalBase>
        </Modal>
      )}
      {showModalContactValidation && (
        <Modal>
          <ModalBase
            openModal={showModalContactValidation}
            width='447.98px'
            backgroundWrapper='white'
            withoutCloseButton
          >
            <ContactValidationModal
              onClose={onCloseContactValidationModal}
              setFinishedAction={setFinishedAction}
              publisherTypeId={publisherTypeId}
            />
          </ModalBase>
        </Modal>
      )}
      {showModalReputationLevel && (
        <Modal>
          <ModalBase
            openModal={showModalReputationLevel}
            onClose={() => {
              Cookies.set('reputationModalLevelSeen2', true, { expires: 400 });
              setShowModalReputationLevel(false);
              sendCustomTrackingInfo({
                event: 'click_modal',
                content_group: 'Panel',
                user_reputation: levelValue,
                user_id: userLoggedId,
                item_id: 'Salir',
                modal_name: 'Modal presentacion Score'
              });
            }}
            topPosition='16px'
            rightPosition='16px'
            withoutOutsideClose
          >
            <ReputationLevelModal
              onClose={onCloseReputationLevelModal}
              userLoggedId={userLoggedId}
              pathname={pathname}
            />
          </ModalBase>
        </Modal>
      )}
    </>
  );
}
export default HeaderDesktop;
